import React, { useRef, memo, useEffect, useState } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import listAnalytics from './listAnalytics';
import TemplateParser from '../../components/Templates/TemplateParser';
import { updatePagination } from '../../actions/environment';
import { unique } from '../../utils/array';
import { addEvent, removeEvent, offset, height } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { decode, urlify } from '../../utils/string';
import s from './Pagination.scss';

const areEqual = (prevProps, nextProps) => {
	if (prevProps.list.length === nextProps.list.length) return true;
	return false;
};

const areCollectionsEqual = (prevProps, nextProps) => {
	if (prevProps.currentCollection.length === nextProps.currentCollection.length) return true;
	return false;
};

const Collection = memo(({ currentCollection, collection, title, list, cache, collectionSections, setup, variantClasses, ListComponent, currentView, canonical, currentPath, currentHash, closeSearch, layout }) => {
	const itemsShowing = [];
	let inlineCategoryContentDirection = 'right';
	let collectionCount = 0;
	let skipProducts = 0;
	let subComponent = [];

	const listing = currentCollection.map((item) => {
		let items = false;
		let id;
		if (list) {
			id = item;
			item = cache[item];
			if (!item) items = Object.keys(cache).filter((cacheID) => cacheID.indexOf(id) !== -1);
		}
		const component = [];

		if (collectionSections?.[collection]?.[collectionCount]) {
			const inlineCategoryContent = collectionSections[collection][collectionCount].sections.filter((section) => section.acfCategory);
			if (inlineCategoryContent.length > 0 && inlineCategoryContent?.[0]?.acfCategory?.content) {
				const tempContent = inlineCategoryContent[0].acfCategory.content;
				const inlineSections = !Array.isArray(tempContent) ? [{ content: { inlineMedia: tempContent.inlineMedia } }] : tempContent;

				if (setup.breakPoint !== 'mobile') {
					skipProducts = inlineSections.filter((section) => section.content).reduce((acc, section) => (
						parseInt(section?.content?.inlineMedia?.products, 10)
					), 0);
				}
				if (setup.breakPoint === 'tablet' && skipProducts !== 0) skipProducts = 0;
				inlineCategoryContentDirection = inlineSections.filter((section) => section.content).reduce((acc, section) => (
					section?.content?.inlineMedia?.position
				), '');
			}

			const inlineCategoryContentTemplate = <TemplateParser key={`content_${collectionCount}`} sections={collectionSections[collection][collectionCount].sections} setup={setup} inline inlineCategoryContent={inlineCategoryContentDirection} />;
			if (skipProducts === 0 && inlineCategoryContent.length > 0) component.push(<div className={[s.contentProductCombo, s.contentProductCombo__left].join(' ')}>{ inlineCategoryContentTemplate }</div>);
			else component.push(inlineCategoryContentTemplate);

			collectionCount += 1;
		}
		if (skipProducts === 0) subComponent = [];

		if (items) {
			items.forEach((variantID) => {
				const variant = cache[variantID];
				itemsShowing.push(variant);
				component.push(<ListComponent variantClasses={[...variantClasses, skipProducts > 0 ? 'productItem__inlineCategoryContent' : null]} key={variantID} id={variantID} content={variant} canonical={canonical} setup={setup} currentPath={currentPath} currentView={currentView} itemIndex={collectionCount + 2} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />);
				collectionCount += 1;
			});
		} else if (item) {
			itemsShowing.push(item);
			component.push(<ListComponent variantClasses={[...variantClasses, skipProducts > 0 ? 'productItem__inlineCategoryContent' : null]} key={list ? id : urlify(translate(item.uri, setup.language).text)} id={id} content={item} canonical={canonical} setup={setup} currentPath={currentPath} currentView={currentView} itemIndex={collectionCount + 2} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />);
			collectionCount += 1;
		}

		// Products included in CMS template.
		if (skipProducts > 0) {
			skipProducts -= 1;
			collectionCount += 1;
			subComponent = [...subComponent, ...component];
			return skipProducts === 0 ? <div key={collectionCount} className={[s.contentProductCombo, s.contentProductCombo__left].join(' ')}>{ subComponent }</div> : null;
		}
		return component;
	});

	// Change to only send products which are visible.
	// Batch send them.
	// Clear queue
	if (itemsShowing.length > 0) {
		listAnalytics(itemsShowing, setup, currentView);
	}

	return (
		<section className={s.pagination_collection}>
			<h1 className={s.pagination_collection_title}>{ title }</h1>
			{ listing }
		</section>
	);
}, areCollectionsEqual);

const nullFunction = () => {};
const emptyArray = [];

const Pagination = ({
	ListComponent,
	collectionSections = {},
	variantClasses = emptyArray,
	collectionOrder = emptyArray,
	setup,
	location,
	list,
	cache,
	currentPath,
	currentView,
	currentHash = '',
	canonical = false,
	filterOpen = false,
	closeSearch = nullFunction,
	layout = 'default',
}) => {
	const paginationContainer = useRef(null);
	const pageBase = unique(list || cache);
	const collectionsPerPage = 2;
	const initialPage = typeof document === 'undefined' ? 100 : 1;
	const [page, updatedPage] = useState(initialPage);

	useEffect(() => {
		let debounced = false;

		addEvent(window, 'scroll.pagination', () => {
			window.requestAnimationFrame(() => {
				if (!debounced && paginationContainer.current) {
					const paginationTop = offset(paginationContainer.current);
					const paginationHeight = height(paginationContainer.current);

					// Loads in when one window away.
					if (window.pageYOffset + (height(window) * 2) > paginationTop.top + paginationHeight) {
						updatedPage((prevState) => prevState + 1);
						debounced = true;
						window.setTimeout(() => {
							debounced = false;
						}, 500);
					}
				}
			});
		});

		return () => {
			removeEvent(window, 'scroll.pagination');
		};
	}, []);

	// Collect products based on collection only use primary products
	const collectionOrderMapped = collectionOrder.map((a) => urlify(decode(a.value)));
	const collections = pageBase.reduce((acc, item) => {
		const tempItem = cache[item];
		if (!tempItem) return acc;
		const tempCollection = urlify(tempItem.collection);
		if (!tempItem.primary && location.type !== 'SEARCH') return acc;
		if (!acc[tempCollection]) acc[tempCollection] = [];
		acc[tempCollection].push(item);
		return acc;
	}, {});

	let paginationClasses = [s.pagination];
	if (filterOpen) paginationClasses.push(s.pagination__filterOpen);
	if (pageBase.length < 5) paginationClasses.push(s.pagination__centered);
	if (variantClasses) paginationClasses = [...paginationClasses, ...(Array.isArray(variantClasses) ? variantClasses.filter((c) => !!c && c.indexOf('pagination__') !== -1).map((c) => s[c]) : [s[variantClasses.toString()]])];

	const collectionListing = Object.keys(collections).sort((a, b) => {
		if (collectionOrderMapped.indexOf(a) === collectionOrderMapped.indexOf(b)) return 0;
		if (collectionOrderMapped.indexOf(a) > collectionOrderMapped.indexOf(b)) return 1;
		return -1;
	});

	return (
		<div className={paginationClasses.join(' ')} ref={paginationContainer}>
			{
				collectionListing.slice(0, collectionsPerPage * page).map((collection) => <Collection key={collection} currentCollection={collections[collection]} collection={collection} title={decode(collectionOrder.filter((c) => urlify(decode(c.value)) === collection)?.[0]?.value)} list={list} cache={cache} collectionSections={collectionSections} setup={setup} variantClasses={variantClasses} ListComponent={ListComponent} currentView={currentView} canonical={canonical} currentPath={currentPath} currentHash={currentHash} closeSearch={closeSearch} layout={layout} />)
			}
		</div>
	);
};

const mapState = ({ environment, location }, { showAll = false, paging = null, currentHash = '', canonical = false, magazine = false, ...props }) => ({
	pagination: environment.pagination,
	setup: environment.setup,
	collectionOrder: environment.collections.order,
	pdpSwatches: environment.pdpSwatches,
	location,
	showAll,
	paging,
	currentHash,
	canonical,
	magazine,
	...props,
});

export default hot(module)(connect(mapState, { updatePagination })(memo(Pagination, areEqual)));
